import { QueryClient } from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import {
  RouterProvider,
  createRootRouteWithContext,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import HomePage from "./routes/Home";
import { allMenusQueryOptions, ClosedError } from "./hooks/useMenu";
import MenuPage from "./routes/Menu";
import NotFound from "./routes/NotFound";
import { CheckoutPage } from "./routes/Checkout";
import { SuccessPage } from "./routes/Success";
import Closed from "./routes/Closed";
import { Suspense } from "react";
import { Toaster } from "sonner";
import { VivaPage } from "./routes/Viva";
import { LocationPage } from "./routes/Location";
import { LocationLayout } from "./routes/Location/layout";
import { MenuLayout } from "./routes/Menu/layout";
import { tablesQueryOptions } from "./hooks/useTables";
import { LocationContextProvider } from "./providers/LocationContextProvider";
import { LocationConfigurationProvider } from "./providers/LocationConfigurationProvider";
import { ThemeProvider } from "./providers/ThemeProvider";

export const API_HOST =
  import.meta.env.VITE_API_HOST ?? "https://api.table.taxi"; // TODO: ENSURE THIS IS NOT COMMITTED TO GIT

export const queryClient = new QueryClient();
const rootRoute = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({});

const persister = createSyncStoragePersister({
  storage: window.sessionStorage,
});

function errorHandler({ error }: { error: Error }) {
  console.log("Error Handler: An error occurred:", error);
  const isClosed = error instanceof ClosedError;
  console.log("Error Handler: Is Closed Error?", isClosed);
  if (error instanceof ClosedError) {
    return (
      <LocationContextProvider>
        <LocationConfigurationProvider>
          <ThemeProvider />
          <Closed message={error.message} />
        </LocationConfigurationProvider>
      </LocationContextProvider>
    );
  }
  return <NotFound />;
}

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});

export const locationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$locationId",
  errorComponent: errorHandler,

  loader: ({ context: { queryClient }, params: { locationId } }) => {
    queryClient.ensureQueryData(allMenusQueryOptions(locationId));
    queryClient.ensureQueryData(tablesQueryOptions(locationId, true));
  },

  component: () => <LocationLayout />,
});

export const locationIndexRoute = createRoute({
  getParentRoute: () => locationRoute,
  path: "/",
  component: () => <LocationPage />,
});

export const menuRoute = createRoute({
  getParentRoute: () => locationRoute,
  path: "$menuId",
  component: () => <MenuLayout />,
});

export const menuIndexRoute = createRoute({
  getParentRoute: () => menuRoute,
  path: "/",
  loader: ({ context: { queryClient }, params: { locationId } }) => {
    queryClient.ensureQueryData(allMenusQueryOptions(locationId));
    queryClient.ensureQueryData(tablesQueryOptions(locationId, true));
  },

  component: () => <MenuPage />,
});

export const vivaRoute = createRoute({
  getParentRoute: () => menuRoute,
  path: "viva/$status",
  component: () => <VivaPage />,
});

export const checkoutRoute = createRoute({
  getParentRoute: () => menuRoute,
  path: "checkout",
  component: () => <CheckoutPage />,
});
export const successRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$locationId/success/$paymentId",
  component: (props) => (
    <Suspense>
      <SuccessPage {...props} />
    </Suspense>
  ),
});

const routeTree = rootRoute.addChildren([
  locationRoute.addChildren([
    locationIndexRoute,
    menuRoute.addChildren([
      menuIndexRoute,
      checkoutRoute,
      vivaRoute,
    ]),
  ]),
  successRoute,
  indexRoute,
]);

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  context: {
    queryClient,
  },
});

function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister }}
    >
      <RouterProvider router={router} />
      <Toaster position="top-right" closeButton duration={4000} />
    </PersistQueryClientProvider>
  );
}

export default App;
